import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  UserPlus,
  Crown,
  Loader2,
  Lock,
  Sparkles,
  Mail,
  Copy,
  Check,
  Trash2,
  LogOut,
  Home,
  RefreshCw,
  Clock
} from 'lucide-react';

const HouseholdSection = () => {
  const { api, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [household, setHousehold] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  
  // Create household dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [householdName, setHouseholdName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Polling state
  const [lastUpdate, setLastUpdate] = useState(null);

  // Load household data
  const loadHousehold = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/household');
      setIsPro(response.data.is_pro);
      setHousehold(response.data.household);
      setIsOwner(response.data.is_owner);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load household:', error);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadHousehold();
  }, [loadHousehold]);

  // Polling for updates every 30 seconds when in a household
  useEffect(() => {
    if (!household) return;
    
    const interval = setInterval(() => {
      loadHousehold();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [household, loadHousehold]);

  const createHousehold = async () => {
    if (!householdName.trim()) {
      toast.error('Please enter a household name');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/household', { name: householdName.trim() });
      toast.success(response.data.message);
      setHousehold(response.data.household);
      setIsOwner(true);
      setCreateOpen(false);
      setHouseholdName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create household');
    } finally {
      setCreating(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await api.post('/household/invite', {
        email: inviteEmail.trim(),
        name: inviteName.trim() || null
      });
      toast.success(response.data.message);
      setLastInviteUrl(response.data.invite_url);
      setInviteEmail('');
      setInviteName('');
      loadHousehold(); // Refresh to show pending invite
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const removeMember = async (userId, memberName) => {
    if (!confirm(`Remove ${memberName} from the household?`)) return;
    
    try {
      await api.delete(`/household/members/${userId}`);
      toast.success('Member removed');
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const leaveHousehold = async () => {
    const message = isOwner 
      ? 'This will delete the household for everyone. Are you sure?'
      : 'Are you sure you want to leave this household?';
    
    if (!confirm(message)) return;
    
    try {
      await api.delete('/household');
      toast.success(isOwner ? 'Household deleted' : 'You left the household');
      setHousehold(null);
      setIsOwner(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave household');
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api.delete(`/household/invite/${inviteId}`);
      toast.success('Invitation cancelled');
      loadHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // Show upgrade prompt for non-Pro users
  if (!isPro) {
    return (
      <Card className="border-dashed border-2 border-muted-foreground/20">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Family Collaboration
            <Badge variant="secondary" className="ml-2">
              <Lock className="h-3 w-3 mr-1" />
              Pro
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold mb-2">Plan Meals as a Family</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Create a household and invite family members to plan meals together. Share grocery lists, pantry items, and budgets.
            </p>
            <Button onClick={() => navigate('/subscription')} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show create household prompt if not in one
  if (!household) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Family Collaboration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Home className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">Create Your Household</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Start a household to invite family members and plan meals together.
            </p>
            
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-household-btn">
                  <Plus className="h-4 w-4" />
                  Create Household
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Create Household</DialogTitle>
                  <DialogDescription>
                    Give your household a name (e.g., &quot;Smith Family&quot;)
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label>Household Name</Label>
                  <Input
                    placeholder="e.g., Smith Family"
                    value={householdName}
                    onChange={(e) => setHouseholdName(e.target.value)}
                    className="mt-2"
                    data-testid="household-name-input"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={createHousehold} disabled={creating} data-testid="confirm-create-btn">
                    {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show household with members
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Home className="h-5 w-5" />
            {household.name}
            {isOwner && <Crown className="h-4 w-4 text-yellow-500" />}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={loadHousehold} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* Invite Button */}
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="invite-member-btn">
                  <UserPlus className="h-4 w-4 mr-1" />
                  Invite
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Invite Family Member</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join your household
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      placeholder="family@email.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="mt-2"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Their Name (optional)</Label>
                    <Input
                      placeholder="e.g., Mom"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  {lastInviteUrl && (
                    <div className="p-3 rounded-lg bg-muted/50 border">
                      <p className="text-sm font-medium mb-2">Or share this link:</p>
                      <div className="flex gap-2">
                        <Input value={lastInviteUrl} readOnly className="text-xs bg-background" />
                        <Button size="icon" variant="outline" onClick={copyInviteLink}>
                          {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteOpen(false)}>Close</Button>
                  <Button onClick={inviteMember} disabled={inviting} data-testid="send-invite-btn">
                    {inviting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                    Send Invite
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Members List */}
        <div>
          <p className="text-sm font-medium mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Members ({household.members?.length || 0})
          </p>
          <div className="space-y-2">
            {household.members?.map((member) => (
              <div 
                key={member.user_id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/30 group"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {member.name}
                      {member.role === 'owner' && (
                        <Crown className="h-3 w-3 text-yellow-500" />
                      )}
                      {member.user_id === user?.id && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </div>
                {isOwner && member.role !== 'owner' && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    onClick={() => removeMember(member.user_id, member.name)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Pending Invites */}
        {household.pending_invites?.length > 0 && (
          <div className="pt-3 border-t">
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending Invites
            </p>
            <div className="space-y-2">
              {household.pending_invites.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="flex items-center justify-between p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20"
                >
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm">{invite.email}</span>
                  </div>
                  {isOwner && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs text-muted-foreground"
                      onClick={() => cancelInvite(invite.invite_id)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Sync Info */}
        <div className="pt-3 border-t text-center">
          <p className="text-xs text-muted-foreground">
            🔄 Shared data syncs automatically every 30 seconds
          </p>
          {lastUpdate && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        
        {/* Leave/Delete Household */}
        <div className="pt-3 border-t">
          <Button
            variant="ghost"
            className="w-full text-muted-foreground hover:text-destructive"
            onClick={leaveHousehold}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {isOwner ? 'Delete Household' : 'Leave Household'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default HouseholdSection;
